/* ATHAR SCENTS — Discovery Quiz
 *
 * Three entry paths (from scratch / anchored on a scent you know / buying a
 * gift), a scoring engine that ratios fit over the dimensions actually
 * answered, and three results picked into distinct roles.
 *
 * The section (sections/quiz.liquid) supplies live commerce data — titles,
 * images, URLs, per-variant price and availability, best-seller membership.
 * This file supplies the taste model and the interface.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-quiz]');
  if (!root) return;

  /* ==================================================================
   * Taste profiles
   *
   * `facets` and `sweetness` have no metafield behind them in Shopify
   * admin, so the curated values live here, keyed by product handle.
   * `intensity`, `occasions` and `gender` are also curated so the quiz
   * scores against one reviewed vocabulary rather than the looser
   * free-text metafield values.
   *
   * A product with no entry here still takes part: profileFor() derives a
   * usable profile from the custom.tone / custom.family / custom.intensity /
   * custom.occasions / custom.gender metafields instead. Add new scents
   * below to score them precisely.
   *
   * Format: facets | sweetness | intensity | occasions | gender
   * ================================================================== */
  var PROFILES = {
    abyss:    'aquatic citrus powdery amber|dry|balanced|casual_day|unisex',
    aim:      'woody smoky earthy leather|dry|balanced|evening_out|unisex',
    amulet:   'floral fruity citrus amber woody|balanced|balanced|casual_day|unisex',
    antique:  'woody smoky|dry|bold|formal_event evening_out|unisex',
    apex:     'powdery amber green earthy|dry|balanced|casual_day|unisex',
    blaze:    'woody powdery smoky amber|balanced|bold|evening_out|unisex',
    bond:     'woody sweet green|sweet|balanced|date_night evening_out|masculine',
    brass:    'smoky green citrus sweet leather|balanced|bold|evening_out|masculine',
    canvas:   'citrus earthy woody green|dry|bold|casual_day|unisex',
    caravan:  'smoky spicy amber earthy woody|dry|bold|formal_event|unisex',
    cask:     'sweet woody|sweet|bold|evening_out|unisex',
    chaos:    'smoky woody amber leather|dry|balanced|evening_out|masculine',
    crest:    'citrus green earthy leather powdery|dry|balanced|formal_event|unisex',
    dew:      'floral powdery woody green|dry|soft|casual_day|feminine',
    dune:     'green leather powdery earthy woody smoky|dry|balanced|office_daily signature_everyday evening_out|unisex',
    dusk:     'amber sweet green citrus|sweet|bold|evening_out|unisex',
    ember:    'smoky leather green spicy fruity|dry|balanced|formal_event|unisex',
    focus:    'woody powdery earthy amber|dry|soft|office_daily signature_everyday casual_day|masculine',
    garnet:   'floral spicy smoky leather amber sweet|balanced|bold|date_night evening_out|unisex',
    glass:    'woody amber powdery|balanced|bold|formal_event evening_out|unisex',
    grain:    'spicy woody sweet green smoky|balanced|balanced|formal_event evening_out|unisex',
    haze:     'spicy woody floral smoky leather|balanced|bold|evening_out|unisex',
    honey:    'sweet citrus smoky leather|sweet|bold|evening_out|unisex',
    horizon:  'citrus green aquatic amber|dry|balanced|casual_day|masculine',
    icon:     'green woody|balanced|balanced|office_daily signature_everyday|masculine',
    ink:      'smoky green woody amber leather|dry|bold|evening_out|unisex',
    lunar:    'smoky fruity woody leather|balanced|bold|evening_out|unisex',
    marble:   'spicy sweet amber woody|sweet|balanced|date_night evening_out|unisex',
    mist:     'green citrus fruity powdery|sweet|soft|casual_day|unisex',
    monolith: 'woody earthy green amber|dry|bold|office_daily signature_everyday|unisex',
    myth:     'sweet powdery woody amber spicy|sweet|balanced|formal_event|masculine',
    nectar:   'fruity sweet floral woody|sweet|bold|casual_day|unisex',
    night:    'earthy powdery sweet green|balanced|balanced|casual_day|masculine',
    noble:    'green floral sweet spicy|balanced|bold|formal_event evening_out|masculine',
    orbit:    'floral aquatic leather earthy|balanced|bold|formal_event evening_out|unisex',
    path:     'woody sweet floral earthy smoky|sweet|bold|date_night evening_out|unisex',
    pod:      'sweet powdery amber citrus|sweet|bold|evening_out|unisex',
    resin:    'amber powdery|dry|bold|formal_event evening_out signature_everyday office_daily|unisex',
    roots:    'citrus floral|dry|balanced|evening_out|unisex',
    rum:      'sweet boozy earthy|sweet|bold|evening_out|unisex',
    salt:     'aquatic woody amber|dry|soft|casual_day|unisex',
    shadow:   'smoky earthy woody|dry|bold|casual_day evening_out|unisex',
    shore:    'woody aquatic|dry|soft|casual_day|unisex',
    solo:     'green spicy amber leather smoky|dry|balanced|evening_out|masculine',
    spark:    'smoky sweet leather|dry|bold|evening_out|unisex',
    suede:    'floral amber sweet powdery|balanced|bold|formal_event evening_out|unisex',
    temple:   'floral woody smoky leather|dry|bold|formal_event evening_out|unisex',
    tide:     'citrus aquatic|dry|soft|casual_day|unisex',
    timber:   'woody green earthy amber|dry|balanced|office_daily signature_everyday|masculine',
    tropics:  'earthy woody amber leather smoky|dry|balanced|evening_out|unisex',
    velvet:   'green smoky woody sweet leather|balanced|balanced|evening_out|unisex',
    verdict:  'smoky sweet woody earthy|balanced|bold|office_daily signature_everyday|unisex',
    vision:   'citrus green woody|dry|balanced|casual_day|masculine',
    vow:      'earthy floral smoky leather|dry|balanced|evening_out|unisex',
    voyage:   'citrus sweet smoky|sweet|balanced|casual_day|unisex',
    zest:     'woody leather powdery green smoky|dry|balanced|office_daily signature_everyday|unisex'
  };

  // Anchors are the scents customers are most likely to recognise by their
  // original name, most-recognised first. Labels come from the live
  // custom.original_perfume metafield, so renaming in admin renames here.
  var ANCHOR_HANDLES = [
    'glass', 'grain', 'icon', 'solo', 'bond', 'noble',
    'suede', 'brass', 'dusk', 'temple', 'cask', 'chaos'
  ];

  /* ==================================================================
   * Question set
   * ================================================================== */
  var PATHS = {
    scratch: {
      label: "I'm starting from nothing",
      blurb: 'Five plain questions. No perfume vocabulary needed.',
      questions: ['character', 'sweetness', 'intensity', 'occasion', 'anchor']
    },
    anchor: {
      label: 'I already know a scent I like',
      blurb: "Name what you've worn. We read the structure behind it.",
      questions: ['anchor', 'direction', 'sweetness', 'intensity', 'occasion']
    },
    gift: {
      label: "I'm buying it for someone else",
      blurb: 'Questions about them, not you.',
      questions: ['recipient', 'style', 'occasion', 'intensity', 'format']
    }
  };

  var QUESTIONS = {
    character: {
      id: 'character', dim: 'character', multi: 2,
      prompt: 'When someone gets close, what should they find?',
      note: 'Choose one or two.',
      options: [
        { id: 'warm',   label: 'Warmth',              hint: 'vanilla, tonka, amber',      facets: ['sweet', 'amber'] },
        { id: 'clean',  label: 'Clean air',           hint: 'citrus, salt, cool woods',   facets: ['citrus', 'aquatic'] },
        { id: 'wood',   label: 'Dry wood',            hint: 'cedar, sandalwood, vetiver', facets: ['woody', 'earthy'] },
        { id: 'smoke',  label: 'Smoke and resin',     hint: 'oud, incense, leather',      facets: ['smoky', 'leather'] },
        { id: 'spice',  label: 'Spice',               hint: 'cardamom, saffron, pepper',  facets: ['spicy'] },
        { id: 'flower', label: 'Flowers, held back',  hint: 'jasmine, rose, orris',       facets: ['floral', 'powdery'] },
        { id: 'green',  label: 'Green and herbal',    hint: 'lavender, sage, vetiver',    facets: ['green'] }
      ]
    },
    sweetness: {
      id: 'sweetness', dim: 'sweetness',
      prompt: 'Do you want sweetness in it?',
      note: 'The one thing most people already know about themselves.',
      options: [
        { id: 'dry',      label: 'No — keep it dry',     hint: 'wood, smoke, salt, herbs', value: ['dry'] },
        { id: 'balanced', label: 'A little, not much',   hint: 'warmth without sugar',     value: ['balanced'] },
        { id: 'sweet',    label: 'Yes — I like it sweet', hint: 'vanilla, honey, amber',   value: ['sweet'] }
      ]
    },
    intensity: {
      id: 'intensity', dim: 'intensity',
      prompt: 'How much room should it take?',
      options: [
        { id: 'soft',     label: 'Only when someone is close', hint: 'a trace on the skin', value: ['soft'] },
        { id: 'balanced', label: 'Noticed, not announced',     hint: "arm's length",        value: ['balanced'] },
        { id: 'bold',     label: 'It arrives before you do',   hint: 'fills the room',      value: ['bold'] }
      ]
    },
    occasion: {
      id: 'occasion', dim: 'occasion',
      prompt: 'Where will you wear it most?',
      options: [
        { id: 'work',    label: 'Work, and the daylight hours', value: ['office_daily'] },
        { id: 'casual',  label: 'Weekends and daytime',         value: ['casual_day'] },
        { id: 'evening', label: 'Evenings out',                 value: ['evening_out', 'date_night'] },
        { id: 'formal',  label: 'Formal rooms',                 value: ['formal_event'] },
        { id: 'all',     label: 'One scent for all of it', hint: 'a signature', value: ['signature_everyday'], relaxes: true }
      ]
    },
    anchor: {
      id: 'anchor', dim: 'anchor', multi: 3, optional: true,
      prompt: 'Have you worn any of these?',
      note: 'Optional. It helps us place you — we read the structure, not the label.',
      options: [] // filled from ANCHOR_HANDLES against the live catalogue
    },
    recipient: {
      id: 'recipient', dim: 'gender',
      prompt: 'Who is it for?',
      options: [
        { id: 'him',    label: 'A man',                   value: ['masculine', 'unisex'] },
        { id: 'her',    label: 'A woman',                 value: ['feminine', 'unisex'] },
        { id: 'either', label: "I'd rather not narrow it", value: ['masculine', 'feminine', 'unisex'] }
      ]
    },
    style: {
      id: 'style', dim: 'character',
      prompt: 'How would you describe the way they dress?',
      options: [
        { id: 'plain',   label: 'Plain, and well made',    facets: ['woody', 'citrus'] },
        { id: 'sharp',   label: 'Sharp. Considered.',      facets: ['woody', 'spicy', 'earthy'] },
        { id: 'warm',    label: 'Warm. A little unexpected.', facets: ['sweet', 'amber', 'spicy'] },
        { id: 'classic', label: 'Traditional',             facets: ['smoky', 'leather', 'amber'] },
        { id: 'unsure',  label: "I honestly don't know",   facets: [], skip: true }
      ]
    },
    format: {
      id: 'format', dim: 'format',
      prompt: 'Would you rather choose for them, or let them choose?',
      note: 'There is no wrong answer here.',
      options: [
        { id: 'choose', label: "I'll choose",     hint: 'three bottles, ranked', value: ['bottle'] },
        { id: 'let',    label: 'Let them choose', hint: 'a set of three 10 ml — they find their own', value: ['set'] }
      ]
    },
    direction: {
      id: 'direction', dim: 'direction',
      prompt: 'What would you change about it?',
      options: [
        { id: 'same',    label: 'Nothing — that register was right', value: [] },
        { id: 'deeper',  label: 'More depth',        value: ['smoky', 'leather', 'earthy'] },
        { id: 'fresher', label: 'Lighter, fresher',  value: ['citrus', 'aquatic', 'green'] },
        { id: 'warmer',  label: 'More warmth',       value: ['sweet', 'amber'] },
        { id: 'quieter', label: 'Less of it — quieter', value: [], intensity_shift: -1 }
      ]
    }
  };

  var ARCHETYPES = [
    { id: 'open_room', name: 'The Open Room', facets: ['citrus', 'aquatic'], box: 'modern-explorer',
      line: 'You want air around you. Salt, citrus and cool wood — presence that reads as ease rather than effort.' },
    { id: 'dry_season', name: 'The Dry Season', facets: ['woody', 'earthy', 'green'], box: 'modern-explorer',
      line: 'You want to be found, not announced. Cedar and vetiver over a dry foundation — structure that holds through a working day.' },
    { id: 'long_evening', name: 'The Long Evening', facets: ['sweet', 'amber', 'boozy'], box: 'warm-sophistication',
      line: 'Warmth that stays after the room empties. Vanilla, amber and resin, weighted toward the foundation.' },
    { id: 'warm_threshold', name: 'The Warm Threshold', facets: ['spicy', 'amber'], box: 'warm-sophistication',
      line: 'Spice held in proportion. Cardamom and saffron over amber — warm without turning sweet.' },
    { id: 'shadowed_hall', name: 'The Shadowed Hall', facets: ['smoky', 'leather'], box: 'power-presence',
      line: 'The darkest register in the collection. Oud, incense and leather — a trace that arrives before you and stays after.' },
    { id: 'held_note', name: 'The Held Note', facets: ['floral', 'powdery', 'fruity'], box: 'warm-sophistication',
      line: 'Flowers kept under restraint. Orris and musk drawn close to the skin, structured rather than pretty.' }
  ];

  /* ==================================================================
   * Matching engine — pure functions over (answers, catalogue)
   * ================================================================== */
  var WEIGHTS = { character: 3, anchor: 3, sweetness: 2, intensity: 2, occasion: 2 };

  // Minimum share of the answered weight a product must reach to be shown.
  // A ratio, not an absolute, so it holds however many questions were skipped.
  var FIT_FLOOR = 0.42;
  var MAX_RESULTS = 3;

  // Everything that enters a comparison goes through here, so a trailing
  // space or a stray capital in a metafield can never silently zero a
  // dimension.
  function canon(v) {
    return String(v == null ? '' : v)
      .normalize('NFKD').replace(/[̀-ͯ]/g, '')
      .trim().toLowerCase()
      .replace(/\s*[|/]\s*/g, '|').replace(/\s+/g, ' ');
  }

  function canonList(list) {
    return (list || []).map(canon).filter(Boolean);
  }

  function uniq(list) {
    return list.filter(function (v, i, a) { return a.indexOf(v) === i; });
  }

  // Stable 32-bit hash, used only for tie-breaking, so the same answers
  // always produce the same order and a shared link stays reproducible.
  function hash(str) {
    var h = 2166136261, i;
    for (i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h * 16777619) >>> 0;
    }
    return h;
  }

  // The 3 ml tester sits at 0 across the catalogue, so it must never be
  // offered as a purchase route. Availability is read per variant: a scent
  // whose 55 ml is out of stock is still buyable as a 10 ml.
  function pickVariants(product) {
    var v = product.variants || {};
    return {
      sample: v.sample && v.sample.available && v.sample.price > 0 ? v.sample : null,
      full: v.full && v.full.available && v.full.price > 0 ? v.full : null
    };
  }

  function isBuyable(product) {
    var v = pickVariants(product);
    return !!(v.sample || v.full);
  }

  // A match on a facet shared by most of the catalogue says almost nothing.
  // A match on a rare one says a lot. Without this, the same handful of
  // broadly-tagged scents win nearly every path.
  function rarity(catalogue) {
    var counts = {}, total = catalogue.length, out = {};
    catalogue.forEach(function (p) {
      canonList(p.facets).forEach(function (f) { counts[f] = (counts[f] || 0) + 1; });
    });
    Object.keys(counts).forEach(function (f) {
      out[f] = Math.log(total / counts[f]) / Math.log(total);
    });
    return out;
  }

  // Every path fills the same target vector, so the scorer never branches.
  function buildTarget(answers, catalogue) {
    var t = {
      character: [], sweetness: [], intensity: [], occasion: [],
      anchorIds: [], answered: {}, relaxOccasion: false, intensityShift: 0
    };
    var byId = {};
    catalogue.forEach(function (p) { byId[p.id] = p; });

    Object.keys(answers).forEach(function (qid) {
      var q = QUESTIONS[qid];
      var picked = [].concat(answers[qid] || []);
      if (!q || !picked.length) return;

      var opts = q.options.filter(function (o) { return picked.indexOf(o.id) !== -1; });
      if (!opts.length) return;

      // An explicit "I don't know" is not an answer — it must not count
      // toward the denominator, or it silently penalises every product.
      if (opts.every(function (o) { return o.skip; })) return;

      opts.forEach(function (o) {
        if (o.relaxes) t.relaxOccasion = true;
        if (o.intensity_shift) t.intensityShift += o.intensity_shift;

        if (q.dim === 'character' || q.dim === 'direction') {
          t.character = t.character.concat(canonList(o.facets || o.value));
        } else if (q.dim === 'anchor') {
          // The anchor carries the taste of the scent they named, not the
          // scent itself — that is what stops it returning the literal dupe.
          var src = byId[o.productId];
          if (src) {
            t.anchorIds.push(src.id);
            t.character = t.character.concat(canonList(src.facets));
          }
        } else if (t[q.dim]) {
          t[q.dim] = t[q.dim].concat(canonList(o.value));
        }
      });

      if (q.dim !== 'format' && q.dim !== 'gender') {
        t.answered[q.dim === 'direction' ? 'character' : q.dim] = true;
      }
      if (q.dim === 'gender') t.gender = canonList(opts[0].value);
      if (q.dim === 'format') t.format = canonList(opts[0].value)[0];
    });

    ['character', 'sweetness', 'intensity', 'occasion'].forEach(function (k) {
      t[k] = uniq(t[k]);
    });

    // "Less of it" shifts the intensity target down a step rather than
    // filtering, so it narrows taste without emptying the shortlist.
    if (t.intensityShift < 0 && t.intensity.length) {
      var ladder = ['soft', 'balanced', 'bold'];
      t.intensity = uniq(t.intensity.map(function (v) {
        var i = ladder.indexOf(v);
        return i > 0 ? ladder[i - 1] : v;
      }));
    }
    return t;
  }

  function scoreProduct(product, target, rare) {
    var matched = {}, earned = 0, possible = 0, hits = [];

    if (target.answered.character && target.character.length) {
      possible += WEIGHTS.character;
      var pf = canonList(product.facets);
      var overlap = target.character.filter(function (f) { return pf.indexOf(f) !== -1; });
      if (overlap.length) {
        // The rarest shared facet decides the strength of the match, and how
        // much of the target it covers decides the rest.
        var best = Math.max.apply(null, overlap.map(function (f) { return rare[f] || 0.4; }));
        var cover = overlap.length / target.character.length;
        matched.character = overlap;
        hits = overlap;
        earned += WEIGHTS.character * (0.55 + 0.45 * cover) * (0.6 + 0.4 * best * 2);
      }
    }

    if (target.answered.sweetness) {
      possible += WEIGHTS.sweetness;
      var ps = canon(product.sweetness);
      if (target.sweetness.indexOf(ps) !== -1) {
        matched.sweetness = true;
        earned += WEIGHTS.sweetness;
      } else {
        // Adjacent on the ladder is a near miss, not a miss. Only the
        // opposite end scores nothing.
        var lad = ['dry', 'balanced', 'sweet'];
        var want = lad.indexOf(target.sweetness[0]);
        var got = lad.indexOf(ps);
        if (want > -1 && got > -1 && Math.abs(want - got) === 1) {
          earned += WEIGHTS.sweetness * 0.45;
        }
      }
    }

    if (target.answered.intensity) {
      possible += WEIGHTS.intensity;
      var pi = canon(product.intensity);
      if (target.intensity.indexOf(pi) !== -1) {
        matched.intensity = true;
        earned += WEIGHTS.intensity;
      } else {
        var il = ['soft', 'balanced', 'bold'];
        var wi = il.indexOf(target.intensity[0]);
        var gi = il.indexOf(pi);
        if (wi > -1 && gi > -1 && Math.abs(wi - gi) === 1) {
          earned += WEIGHTS.intensity * 0.45;
        }
      }
    }

    if (target.answered.occasion) {
      possible += WEIGHTS.occasion;
      var po = canonList(product.occasions);
      if (target.relaxOccasion) {
        // "One scent for all of it" is a preference for versatility, not a
        // filter — reward breadth instead of a specific slot.
        matched.occasion = true;
        earned += WEIGHTS.occasion * Math.min(1, po.length / 2);
      } else if (po.some(function (o) { return target.occasion.indexOf(o) !== -1; })) {
        matched.occasion = true;
        earned += WEIGHTS.occasion;
      }
    }

    // A soft steer, never a filter — most of the catalogue is unisex.
    if (target.gender && target.gender.length) {
      if (target.gender.indexOf(canon(product.gender)) === -1) earned *= 0.72;
    }

    // Don't hand back the exact thing they told us they already own.
    if (target.anchorIds.indexOf(product.id) !== -1) earned *= 0.25;

    return {
      product: product,
      fit: possible ? earned / possible : 0,
      matched: matched,
      hits: hits,
      rarest: hits.length
        ? hits.slice().sort(function (a, b) { return (rare[b] || 0) - (rare[a] || 0); })[0]
        : null
    };
  }

  // Three named roles rather than "top 3". Guarantees the cards differ by
  // construction, and gives each one a reason to exist that can be stated in
  // plain language on the card.
  var ROLES = [
    { id: 'match',     label: 'The closest match' },
    { id: 'safer',     label: 'The easier one to live with' },
    { id: 'departure', label: 'The one that goes further' }
  ];

  function select(scored, seed, target) {
    var pool = scored.filter(function (s) { return s.fit >= FIT_FLOOR; });

    // Never let the floor produce an empty page when real candidates exist.
    if (pool.length < MAX_RESULTS) {
      pool = scored.filter(function (s) { return s.fit >= FIT_FLOOR - 0.12; });
    }
    if (pool.length < MAX_RESULTS) pool = scored.slice();

    // Character is what the user actually asked for. Relaxing the floor to
    // fill three slots must never quietly promote something that ignores it.
    // If ANY scent in the catalogue matches the requested character, the top
    // slot has to be one of them.
    if (target && target.answered.character) {
      var onCharacter = scored.filter(function (s) { return s.matched.character; });
      if (onCharacter.length) {
        var inPool = {};
        pool.forEach(function (s) { inPool[s.product.id] = true; });
        onCharacter.forEach(function (s) {
          if (!inPool[s.product.id]) pool.push(s);
        });
      }
    }

    pool.sort(function (a, b) {
      if (Math.abs(b.fit - a.fit) > 0.001) return b.fit - a.fit;
      if (a.product.is_bestseller !== b.product.is_bestseller) {
        return a.product.is_bestseller ? -1 : 1;
      }
      // Deterministic, but not alphabetical — an alphabetical tie-break is a
      // systematic bias toward the front of the catalogue, not a neutral one.
      return hash(a.product.handle + seed) - hash(b.product.handle + seed);
    });

    var picked = [], used = {};
    if (!pool.length) return picked;

    // Slot 1: the best fit that actually carries the requested character.
    var lead = pool[0];
    if (target && target.answered.character) {
      lead = pool.filter(function (s) { return s.matched.character; })[0] || pool[0];
    }
    picked.push(assign({}, lead, { role: ROLES[0] }));
    canonList(lead.product.facets).forEach(function (f) { used[f] = true; });
    pool = pool.filter(function (s) { return s.product.id !== lead.product.id; });
    pool.unshift(lead);

    // Slot 2: high fit, but softer or better-established — the low-regret pick.
    var safer = pool.slice(1).filter(function (s) {
      return s.product.is_bestseller || canon(s.product.intensity) !== 'bold';
    })[0] || pool[1];
    if (safer) {
      picked.push(assign({}, safer, { role: ROLES[1] }));
      canonList(safer.product.facets).forEach(function (f) { used[f] = true; });
    }

    // Slot 3: must introduce a facet neither of the first two carry,
    // otherwise the three cards read as the same scent three times.
    var departure = pool.filter(function (s) {
      if (picked.some(function (p) { return p.product.id === s.product.id; })) return false;
      return canonList(s.product.facets).some(function (f) { return !used[f]; });
    })[0];
    if (!departure) {
      departure = pool.filter(function (s) {
        return !picked.some(function (p) { return p.product.id === s.product.id; });
      })[0];
    }
    if (departure) picked.push(assign({}, departure, { role: ROLES[2] }));

    return picked;
  }

  var FACET_PHRASE = {
    woody: 'dry wood', smoky: 'smoke and resin', sweet: 'sweetness',
    leather: 'leather', amber: 'amber', powdery: 'powder and musk',
    earthy: 'earth and patchouli', green: 'green and herbal',
    citrus: 'citrus', floral: 'flowers', spicy: 'spice',
    aquatic: 'salt and sea air', fruity: 'fruit', boozy: 'spirit'
  };

  // Reasons are generated for the whole set at once, not per card, because
  // "what makes this one different" is only answerable with the others in
  // view. Each card is tried against several axes and takes the first that no
  // sibling has already claimed.
  function reasons(picked) {
    var SWEET_RANK = { dry: 0, balanced: 1, sweet: 2 };
    var INTENS_RANK = { soft: 0, balanced: 1, bold: 2 };
    var claimed = {};
    var out = [];

    function take(key, text) {
      if (!text || claimed[key]) return null;
      claimed[key] = true;
      return text;
    }

    picked.forEach(function (entry, idx) {
      var mine = canonList(entry.product.facets);
      var others = picked.filter(function (s) { return s.product.id !== entry.product.id; });
      var theirFacets = {};
      others.forEach(function (s) {
        canonList(s.product.facets).forEach(function (f) { theirFacets[f] = true; });
      });

      var lines = [];

      // 1. a facet no sibling carries
      var distinct = mine.filter(function (f) { return !theirFacets[f]; });
      if (distinct.length) {
        lines.push(['facet:' + distinct[0],
          fill('only_one_with', 'The only one here with __X__.',
            FACET_PHRASE[distinct[0]] || distinct[0])]);
      }

      // 2. an end of the sweet/dry axis it holds alone
      var ms = SWEET_RANK[canon(entry.product.sweetness)];
      var sweeter = others.every(function (s) { return SWEET_RANK[canon(s.product.sweetness)] < ms; });
      var drier = others.every(function (s) { return SWEET_RANK[canon(s.product.sweetness)] > ms; });
      if (sweeter) lines.push(['sweet', ui('sweetest', 'The sweetest of the three.')]);
      if (drier) lines.push(['dry', ui('driest', 'The driest of the three.')]);

      // 3. an end of the intensity axis it holds alone
      var mi = INTENS_RANK[canon(entry.product.intensity)];
      var loudest = others.every(function (s) { return INTENS_RANK[canon(s.product.intensity)] < mi; });
      var quietest = others.every(function (s) { return INTENS_RANK[canon(s.product.intensity)] > mi; });
      if (loudest) lines.push(['loud', ui('loudest', 'The one that carries furthest.')]);
      if (quietest) lines.push(['quiet', ui('quietest', 'The closest to the skin.')]);

      // 4. a Foundation note no sibling shares
      var base = (entry.product.notes || {}).foundation || [];
      var theirNotes = {};
      others.forEach(function (s) {
        ((s.product.notes || {}).foundation || []).forEach(function (n) { theirNotes[canon(n)] = true; });
      });
      var ownNote = base.filter(function (n) { return !theirNotes[canon(n)]; })[0];
      if (ownNote) {
        lines.push(['note:' + canon(ownNote),
          fill('in_foundation', '__X__ in the Foundation.', ownNote)]);
      }

      // 5. last resort — its own leading facet, which is always distinct from
      //    the others' leading facets by construction of the slots
      if (mine[0]) {
        lines.push(['lead:' + mine[0] + ':' + idx,
          fill('built_on', 'Built on __X__.', FACET_PHRASE[mine[0]] || mine[0])]);
      }

      var head = null, headKey = null;
      for (var i = 0; i < lines.length && !head; i++) {
        head = take(lines[i][0], lines[i][1]);
        if (head) headKey = lines[i][0];
      }

      // Second sentence: where the character sits, if not already said.
      var tail = '';
      var joiner = ui('and', ' and ');
      var foundation = base.slice(0, 2).join(joiner);
      var structure = ((entry.product.notes || {}).structure || []).slice(0, 2).join(joiner);
      // Don't repeat the Foundation if the head sentence already named it.
      // This used to test head.indexOf('Foundation'), which is true only in
      // English — the head key says the same thing in any language.
      if (head && headKey && headKey.indexOf('note:') !== 0) {
        if (foundation && !claimed['tail:' + canon(foundation)]) {
          claimed['tail:' + canon(foundation)] = true;
          tail = fill('in_foundation', '__X__ in the Foundation.', foundation);
        } else if (structure && !claimed['tail:' + canon(structure)]) {
          claimed['tail:' + canon(structure)] = true;
          tail = fill('in_structure', '__X__ in the Structure.', structure);
        }
      }

      out.push([head, tail].filter(Boolean).join(' '));
    });

    return out;
  }

  function band(fit) {
    if (fit >= 0.85) return ui('band_direct', 'Direct');
    if (fit >= 0.65) return ui('band_close', 'Very close');
    return ui('band_near', 'Close');
  }

  function archetypeFor(results, target, rare) {
    var weight = {};
    // Weight what they asked for above what they were shown, but let the
    // results break a tie.
    target.character.forEach(function (f) { weight[f] = (weight[f] || 0) + 3; });
    results.forEach(function (r, i) {
      canonList(r.product.facets).forEach(function (f) { weight[f] = (weight[f] || 0) + (3 - i); });
    });

    var best = null, bestScore = -1;
    ARCHETYPES.forEach(function (a) {
      // Rarity-weighted. Without it the headline goes to whichever archetype
      // names the most common facets — woody/earthy/green covers most of the
      // catalogue, so "The Dry Season" came back for 62% of all answer sets.
      var s = canonList(a.facets).reduce(function (acc, f) {
        return acc + (weight[f] || 0) * (rare[f] || 0.4);
      }, 0);
      if (s > bestScore) { bestScore = s; best = a; }
    });
    return best;
  }

  function run(answers, catalogue) {
    var pool = catalogue.filter(isBuyable);
    var target = buildTarget(answers, catalogue);
    var rare = rarity(pool);
    var seed = JSON.stringify(answers);

    var scored = pool.map(function (p) { return scoreProduct(p, target, rare); });
    var picked = select(scored, seed, target);
    var why = reasons(picked);

    picked.forEach(function (r, i) {
      r.why = why[i];
      r.band = band(r.fit);
      r.variants = pickVariants(r.product);
    });

    return {
      results: picked,
      archetype: archetypeFor(picked, target, rare),
      target: target,
      considered: pool.length
    };
  }

  /*
   * How many scents still satisfy EVERY answer given so far.
   *
   * Deliberately not the same measure as scoring. `fit` is a ratio over the
   * dimensions answered, so adding an answer that most scents satisfy can
   * RAISE the average and push the count up — which would make the on-screen
   * counter climb and destroy the sense of narrowing.
   *
   * Each ANSWERED QUESTION becomes its own filter and they are ANDed, rather
   * than merging every character-ish answer into one shared OR list. Merging
   * is what lets a later answer widen the set: anchoring on Oud Wood and then
   * asking for "lighter, fresher" would union both facet lists and admit more
   * scents than the anchor alone. Question-by-question, an answer can only
   * ever remove candidates, so the number falls monotonically and "still fit"
   * is literally true.
   */
  function narrow(answers, catalogue) {
    var pool = catalogue.filter(isBuyable);
    var keys = Object.keys(answers);
    if (!keys.length) return pool.length;

    var byId = {};
    catalogue.forEach(function (p) { byId[p.id] = p; });

    // Collected before the filters are built: "quieter" is expressed on the
    // direction question but applies to the intensity one.
    var shift = 0;
    keys.forEach(function (qid) {
      var q = QUESTIONS[qid];
      if (!q) return;
      q.options.forEach(function (o) {
        if (o.intensity_shift && [].concat(answers[qid] || []).indexOf(o.id) !== -1) shift += o.intensity_shift;
      });
    });

    var filters = [];
    keys.forEach(function (qid) {
      var q = QUESTIONS[qid];
      var picked = [].concat(answers[qid] || []);
      if (!q || !picked.length) return;
      var opts = q.options.filter(function (o) { return picked.indexOf(o.id) !== -1; });
      if (!opts.length || opts.every(function (o) { return o.skip; })) return;

      var want = [];
      if (q.dim === 'anchor') {
        opts.forEach(function (o) {
          var src = byId[o.productId];
          if (src) want = want.concat(canonList(src.facets));
        });
      } else {
        opts.forEach(function (o) { want = want.concat(canonList(o.facets || o.value)); });
      }
      want = uniq(want);

      if (q.dim === 'character' || q.dim === 'direction' || q.dim === 'anchor') {
        if (!want.length) return;
        filters.push(function (p) {
          var pf = canonList(p.facets);
          return want.some(function (f) { return pf.indexOf(f) !== -1; });
        });
      } else if (q.dim === 'sweetness') {
        filters.push(function (p) { return want.indexOf(canon(p.sweetness)) !== -1; });
      } else if (q.dim === 'intensity') {
        var ladder = ['soft', 'balanced', 'bold'];
        var wanted = shift < 0
          ? uniq(want.map(function (v) {
              var i = ladder.indexOf(v);
              return i > 0 ? ladder[i - 1] : v;
            }))
          : want;
        filters.push(function (p) { return wanted.indexOf(canon(p.intensity)) !== -1; });
      } else if (q.dim === 'occasion') {
        // "One scent for all of it" is a preference for versatility, not a
        // slot to filter on.
        if (opts.some(function (o) { return o.relaxes; })) return;
        filters.push(function (p) {
          var po = canonList(p.occasions);
          return po.some(function (o) { return want.indexOf(o) !== -1; });
        });
      }
    });

    var n = pool.filter(function (p) {
      return filters.every(function (f) { return f(p); });
    }).length;

    // Never show a number below what we will actually deliver — a "2" on
    // screen implies a dead end that is not real.
    return Math.max(n, MAX_RESULTS);
  }

  /* ==================================================================
   * Catalogue — live Shopify data merged with the taste profiles
   * ================================================================== */
  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
    }
    return target;
  }

  function readJSON(selector, fallback) {
    var el = root.querySelector(selector);
    if (!el) return fallback;
    try { return JSON.parse(el.textContent) || fallback; } catch (e) { return fallback; }
  }

  // Shopify's `t` filter HTML-escapes what it returns, so a translation arrives
  // here already carrying entities. esc() would escape them a second time and
  // print a literal &quot; on screen, so unwind them once on the way in. The
  // inverse of esc(); `&amp;` unwinds last so `&amp;lt;` does not become a tag.
  function decodeEntities(s) {
    return String(s == null ? '' : s)
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  // Free-text occasion metafields mapped onto the quiz vocabulary. Only used
  // for scents with no PROFILES entry.
  function mapOccasions(values) {
    var out = [];
    canonList(values).forEach(function (v) {
      if (v.indexOf('office') > -1 || v.indexOf('daily') > -1) out.push('office_daily');
      if (v.indexOf('casual') > -1 || v.indexOf('day out') > -1) out.push('casual_day');
      if (v.indexOf('date night') > -1 || v.indexOf('seduction') > -1) out.push('date_night');
      if (v.indexOf('evening') > -1 || v.indexOf('statement') > -1 || v.indexOf('bold') > -1) out.push('evening_out');
      if (v.indexOf('formal') > -1 || v.indexOf('elegant') > -1 || v.indexOf('luxury') > -1 || v.indexOf('special event') > -1) out.push('formal_event');
      if (v.indexOf('signature') > -1 || v.indexOf('all-rounder') > -1 || v.indexOf('all rounder') > -1) out.push('signature_everyday');
    });
    return uniq(out);
  }

  var TONE_FACETS = {
    fresh: ['citrus', 'aquatic'],
    woody: ['woody', 'earthy'],
    floral: ['floral', 'powdery'],
    amber: ['amber', 'sweet']
  };
  var FAMILY_FACETS = [
    ['leather', 'leather'], ['oriental', 'amber'], ['amber', 'amber'],
    ['woody', 'woody'], ['chypre', 'earthy'], ['citrus', 'citrus'],
    ['fresh', 'citrus'], ['aromatic', 'green'], ['fougere', 'green'],
    ['floral', 'floral']
  ];

  // Derive a usable profile for a scent that has no curated entry, so new
  // products participate in the quiz the moment they are published.
  function derivedProfile(p) {
    var facets = (TONE_FACETS[canon(p.tone)] || []).slice();
    var fam = canon(p.family);
    FAMILY_FACETS.forEach(function (pair) {
      if (fam.indexOf(pair[0]) > -1) facets.push(pair[1]);
    });
    var intensity = canon(p.intensity);
    if (['soft', 'balanced', 'bold'].indexOf(intensity) === -1) intensity = 'balanced';
    var gender = canon(p.gender);
    if (['masculine', 'feminine', 'unisex'].indexOf(gender) === -1) gender = 'unisex';
    return {
      facets: uniq(facets),
      sweetness: 'balanced',
      intensity: intensity,
      occasions: mapOccasions(p.occasions),
      gender: gender
    };
  }

  function profileFor(p) {
    var raw = PROFILES[p.handle];
    if (!raw) return derivedProfile(p);
    var parts = raw.split('|');
    return {
      facets: parts[0] ? parts[0].split(' ') : [],
      sweetness: parts[1],
      intensity: parts[2],
      occasions: parts[3] ? parts[3].split(' ') : [],
      gender: parts[4]
    };
  }

  var CATALOGUE = readJSON('[data-quiz-products]', []).map(function (p) {
    return assign({}, p, profileFor(p));
  });
  var BOXES = readJSON('[data-quiz-boxes]', {});
  var STRINGS = readJSON('[data-quiz-strings]', {});
  // db_hint comes from a `_html` key, which `t` leaves as real markup; decoding
  // is a no-op for it and str() hands it to innerHTML unescaped on purpose.
  Object.keys(STRINGS).forEach(function (k) {
    if (typeof STRINGS[k] === 'string') STRINGS[k] = decodeEntities(STRINGS[k]);
  });

  // Anchors only offer scents that are actually in the catalogue and buyable,
  // so nobody anchors on something we cannot then reason about.
  QUESTIONS.anchor.options = ANCHOR_HANDLES.map(function (handle) {
    var p = CATALOGUE.filter(function (c) { return c.handle === handle && isBuyable(c); })[0];
    if (!p) return null;
    return { id: handle, label: p.original || p.title, productId: p.id };
  }).filter(Boolean);

  var ANCHOR_ENABLED = QUESTIONS.anchor.options.length >= 4;

  /* ==================================================================
   * Localised copy
   *
   * Everything above is the English source of truth. A locale pack
   * (assets/quiz-copy-<locale>.js, loaded by sections/quiz.liquid just
   * before this file) may publish window.ATHAR_QUIZ_COPY to override any
   * of it. Anything a pack omits stays English, so a partial translation
   * degrades to a mixed page rather than a broken one.
   *
   * Anchor options are deliberately not translatable: their labels are the
   * original perfume names from the custom.original_perfume metafield, and
   * those are proper nouns in every language.
   * ================================================================== */
  var COPY = window.ATHAR_QUIZ_COPY || {};

  // Interface strings that are built inline rather than held in a table.
  // Placeholders are __X__ so they survive a round-trip through JSON.
  function ui(key, fallback) {
    var v = COPY.ui && COPY.ui[key];
    return typeof v === 'string' && v ? v : fallback;
  }

  function fill(key, fallback, value) {
    return ui(key, fallback).replace('__X__', value);
  }

  Object.keys(COPY.paths || {}).forEach(function (id) {
    if (PATHS[id]) assign(PATHS[id], COPY.paths[id]);
  });
  Object.keys(COPY.questions || {}).forEach(function (id) {
    var q = QUESTIONS[id], src = COPY.questions[id];
    if (!q) return;
    if (src.prompt) q.prompt = src.prompt;
    if (src.note) q.note = src.note;
    (q.options || []).forEach(function (o) {
      var t = src.options && src.options[o.id];
      if (!t) return;
      if (t.label) o.label = t.label;
      if (t.hint) o.hint = t.hint;
    });
  });
  ARCHETYPES.forEach(function (a) {
    var t = COPY.archetypes && COPY.archetypes[a.id];
    if (!t) return;
    if (t.name) a.name = t.name;
    if (t.line) a.line = t.line;
  });
  ROLES.forEach(function (r) {
    var t = COPY.roles && COPY.roles[r.id];
    if (t) r.label = t;
  });
  assign(FACET_PHRASE, COPY.facets || {});

  function str(key, fallback) {
    return typeof STRINGS[key] === 'string' && STRINGS[key] ? STRINGS[key] : fallback;
  }

  /* ==================================================================
   * Presentation
   * ================================================================== */
  var app = root.querySelector('[data-quiz-app]');
  if (!app || !CATALOGUE.length) return;

  var moneyFormat = root.getAttribute('data-money-format') || 'LE {{amount}}';

  // Minimal Shopify money formatter — covers the standard placeholder set.
  function money(cents) {
    var value = cents / 100;
    var placeholder = /\{\{\s*(\w+)\s*\}\}/.exec(moneyFormat);
    var type = placeholder ? placeholder[1] : 'amount';
    var thousands = ',';
    var decimal = '.';
    if (type === 'amount_with_comma_separator' || type === 'amount_no_decimals_with_comma_separator') {
      thousands = '.';
      decimal = ',';
    } else if (type === 'amount_with_apostrophe_separator') {
      thousands = "'";
    }
    var noDecimals = type.indexOf('no_decimals') !== -1 || Math.round(value * 100) % 100 === 0;
    var parts = value.toFixed(noDecimals ? 0 : 2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var amount = parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // The live quiz emitted nothing at all, so completion rate, per-question
  // drop-off and quiz-to-purchase were unmeasurable. Everything below feeds
  // the same dataLayer the rest of the theme uses.
  window.dataLayer = window.dataLayer || [];
  function track(event, params) {
    window.dataLayer.push(assign({ event: event, quiz_version: 'v2' }, params || {}));
  }

  var state = { path: null, step: 0, answers: {}, order: [] };

  function pathQuestionIds(path) {
    return PATHS[path].questions.filter(function (id) { return id !== 'anchor' || ANCHOR_ENABLED; });
  }

  function stage(name) { root.setAttribute('data-stage', name); }

  /* ------------------------------------------------------------- plate */
  // Facade / Structure / Foundation — never top/heart/base, per brand voice.
  // The brand's own metaphor, drawn rather than described: three rules that
  // gain weight and reach as answers arrive.
  function plate() {
    var a = state.answers;
    var on = {
      facade: !!(a.intensity || a.occasion),
      structure: !!(a.sweetness || a.direction),
      foundation: !!(a.character || a.style || a.anchor)
    };
    var rows = [
      ['facade', ui('plate_facade', 'Facade'), 26],
      ['structure', ui('plate_structure', 'Structure'), 56],
      ['foundation', ui('plate_foundation', 'Foundation'), 86]
    ];

    function weight(k) {
      if (k === 'foundation' && a.intensity) {
        var v = [].concat(a.intensity)[0];
        return v === 'bold' ? 3 : v === 'balanced' ? 2 : 1;
      }
      return on[k] ? 2 : 1;
    }
    function span(k) {
      if (!on[k]) return 40;
      if (k === 'foundation') return 100;
      if (k === 'structure') return 84;
      return 66;
    }

    return '<div class="quiz-plate"><svg viewBox="0 0 300 112" role="img" ' +
      'aria-label="' + esc(ui('plate_aria', 'A drawing of your scent profile, building as you answer')) + '">' +
      rows.map(function (row) {
        var k = row[0], w = span(k) * 2.2, x = 150 - w / 2, y = row[2];
        return '<g class="quiz-plate__layer' + (on[k] ? ' is-on' : '') + '">' +
          '<line x1="' + x + '" y1="' + y + '" x2="' + (x + w) + '" y2="' + y + '" stroke-width="' + weight(k) + '"/>' +
          '<text class="quiz-plate__caption" x="150" y="' + (y - 8) + '" text-anchor="middle">' + row[1] + '</text>' +
          '</g>';
      }).join('') +
      '<line class="quiz-plate__tick" x1="150" y1="32" x2="150" y2="42" stroke-dasharray="2 4"/>' +
      '<line class="quiz-plate__tick" x1="150" y1="62" x2="150" y2="72" stroke-dasharray="2 4"/>' +
      '</svg></div>';
  }

  var lastCount = null;
  function counter() {
    var n = narrow(state.answers, CATALOGUE);
    var prev = lastCount;
    lastCount = n;
    if (!Object.keys(state.answers).length) {
      return '<p class="quiz-counter">' +
        esc(fill('compositions', '__X__ compositions', CATALOGUE.length)) + '</p>';
    }
    // Only narrate the transition when the set actually narrowed. Ticking a
    // second answer inside one multi-select question legitimately widens it,
    // and "43 · 44" reads like a bug even though both numbers are true.
    return '<p class="quiz-counter">' +
      (prev && prev > n ? '<span class="quiz-counter__was">' + prev + '</span> &nbsp;·&nbsp; ' : '') +
      // escape the template first, then drop the (already safe) <b> in
      esc(ui('still_fit', '__X__ still fit')).replace('__X__', '<b>' + n + '</b>') + '</p>';
  }

  /* ------------------------------------------------------------- intro */
  function doorsHtml() {
    return Object.keys(PATHS).map(function (id, i) {
      var p = PATHS[id];
      return '<button type="button" class="quiz-door" data-path="' + esc(id) + '">' +
        '<span class="quiz-door__idx">0' + (i + 1) + '</span>' +
        '<span><span class="quiz-door__title">' + esc(p.label) + '</span>' +
        '<span class="quiz-door__blurb">' + esc(p.blurb) + '</span></span></button>';
    }).join('');
  }

  function renderIntro() {
    lastCount = null;
    stage('intro');
    app.innerHTML = '<section class="quiz-screen quiz-intro">' +
      '<p class="quiz-eyebrow">' + esc(str('label', 'Discovery Quiz')) + '</p>' +
      '<h1 class="quiz-h1">' + esc(str('heading', 'Find the trace that matches your presence')) + '</h1>' +
      '<p class="quiz-lede">' + esc(str('description', '')) + '</p>' +
      '<div class="quiz-doors">' + doorsHtml() + '</div>' +
      '<p class="quiz-fine">' + esc(str('fineprint', 'Every question can be skipped.')) + '</p>' +
      '</section>';
  }

  function start(path) {
    state = { path: path, step: 0, answers: {}, order: pathQuestionIds(path) };
    lastCount = null;
    track('quiz_start', { entry_path: path });
    renderQuestion();
  }

  /* --------------------------------------------------------- questions */
  function renderQuestion() {
    var ids = state.order;
    if (state.step >= ids.length) return renderResults();
    var q = QUESTIONS[ids[state.step]];
    var picked = [].concat(state.answers[q.id] || []);
    var multi = q.multi || 0;
    stage('quiz');

    track('quiz_question_viewed', { question_id: q.id, question_index: state.step + 1 });

    var isChips = q.id === 'anchor';
    var body = isChips
      ? '<div class="quiz-chips" role="group" aria-label="' + esc(q.prompt) + '">' +
          q.options.map(function (o) {
            return '<button type="button" class="quiz-chip" role="checkbox" aria-checked="' +
              (picked.indexOf(o.id) !== -1) + '" data-opt="' + esc(o.id) + '">' + esc(o.label) + '</button>';
          }).join('') + '</div>'
      : '<ul class="quiz-options" role="' + (multi ? 'group' : 'radiogroup') + '" aria-labelledby="QuizPrompt">' +
          q.options.map(function (o) {
            return '<li><button type="button" class="quiz-opt" role="' + (multi ? 'checkbox' : 'radio') +
              '" aria-checked="' + (picked.indexOf(o.id) !== -1) + '" data-opt="' + esc(o.id) + '">' +
              '<span class="quiz-opt__label">' + esc(o.label) + '</span>' +
              (o.hint ? '<span class="quiz-opt__hint">' + esc(o.hint) + '</span>' : '') +
              '</button></li>';
          }).join('') + '</ul>';

    app.innerHTML = '<section class="quiz-screen">' +
      plate() + counter() +
      '<div class="quiz-stepline">' +
        '<div class="quiz-pips" aria-hidden="true">' + ids.map(function (_, i) {
          return '<i class="' + (i < state.step ? 'is-done' : i === state.step ? 'is-now' : '') + '"></i>';
        }).join('') + '</div>' +
        '<span class="quiz-stepno">' + (state.step + 1) + ' / ' + ids.length + '</span>' +
      '</div>' +
      '<h2 class="quiz-prompt" id="QuizPrompt" tabindex="-1">' + esc(q.prompt) + '</h2>' +
      (q.note ? '<p class="quiz-note">' + esc(q.note) + '</p>' : '') +
      body +
      '<div class="quiz-controls">' +
        '<button type="button" class="quiz-ghost" data-quiz-back ' + (state.step === 0 ? 'disabled' : '') + '>' +
          '&larr; ' + esc(str('back', 'Back')) + '</button>' +
        '<div class="quiz-controls__right">' +
          '<button type="button" class="quiz-ghost" data-quiz-skip>' + esc(str('not_sure', 'Not sure')) + '</button>' +
          (multi || isChips
            ? '<button type="button" class="btn btn--primary" data-quiz-next ' + (picked.length ? '' : 'disabled') + '>' +
              esc(str('continue', 'Continue')) + '</button>'
            : '') +
        '</div>' +
      '</div></section>';

    var prompt = app.querySelector('#QuizPrompt');
    if (prompt) prompt.focus();
  }

  // Re-paint option states without stealing focus back to the prompt, so
  // multi-select does not fight a keyboard user.
  function repaintSelection(sel) {
    app.querySelectorAll('[data-opt]').forEach(function (b) {
      b.setAttribute('aria-checked', sel.indexOf(b.getAttribute('data-opt')) !== -1);
    });
    var next = app.querySelector('[data-quiz-next]');
    if (next) next.disabled = !sel.length;
    var c = app.querySelector('.quiz-counter');
    if (c) c.outerHTML = counter();
  }

  function advance(q, answer, skipped) {
    track('quiz_question_answered', {
      question_id: q.id,
      question_index: state.step + 1,
      answer_id: answer,
      is_skip: skipped,
      candidates_remaining: narrow(state.answers, CATALOGUE)
    });
    state.step++;
    renderQuestion();
  }

  /* ----------------------------------------------------------- results */
  function cardHtml(r) {
    var p = r.product, v = r.variants;
    var media = p.image
      ? '<img src="' + esc(p.image) + '" alt="' + esc(p.imageAlt || p.title) + '" loading="lazy" width="380" height="507">'
      : '';
    var buys = [];
    if (v.sample) {
      buys.push('<button type="button" class="btn btn--primary quiz-card__buy" data-add="' + esc(v.sample.id) +
        '" data-size="10ml" data-handle="' + esc(p.handle) + '">' +
        esc(str('try_sample', 'Try 10 ml')) + ' &middot; ' + esc(money(v.sample.price)) + '</button>');
    }
    if (v.full) {
      buys.push('<button type="button" class="btn btn--secondary quiz-card__buy" data-add="' + esc(v.full.id) +
        '" data-size="55ml" data-handle="' + esc(p.handle) + '">' +
        esc(str('full_bottle', 'Full Bottle')) + ' &middot; ' + esc(money(v.full.price)) + '</button>');
    }

    return '<article class="quiz-card">' +
      '<div class="quiz-card__top">' +
        '<a class="quiz-card__media" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' + media + '</a>' +
        '<div class="quiz-card__body">' +
          '<p class="quiz-card__role">' + esc(r.role.label) + '</p>' +
          '<h3 class="quiz-card__title"><a href="' + esc(p.url) + '">' + esc(p.title) + '</a></h3>' +
          (p.original ? '<p class="quiz-card__orig">' +
            esc(str('our_version_of', 'Our version of __ORIGINAL__').replace('__ORIGINAL__', p.original)) + '</p>' : '') +
          '<p class="quiz-card__why">' + esc(r.why) + '</p>' +
          '<div class="quiz-card__meta">' +
            '<span class="chip quiz-card__fit">' + esc(r.band) + '</span>' +
            '<span class="chip">' + esc(p.intensity) + '</span>' +
            '<span class="chip">' + esc(p.sweetness) + '</span>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="quiz-card__buys">' + buys.join('') + '</div>' +
      '</article>';
  }

  function renderResults() {
    var out = run(state.answers, CATALOGUE);
    if (!out.results.length) return renderEmpty();

    var arch = out.archetype;
    var box = BOXES[arch && arch.box];
    stage('results');

    track('quiz_complete', {
      archetype: arch && arch.id,
      result_handles: out.results.map(function (r) { return r.product.handle; }),
      questions_answered: Object.keys(state.answers).length,
      entry_path: state.path
    });

    var samples = out.results.map(function (r) { return r.variants.sample; }).filter(Boolean);
    var sampleTotal = samples.reduce(function (a, v) { return a + v.price; }, 0);

    // The gift-giver's objection is "what if they don't like it?", and the
    // answer is to give a choice rather than a bottle. When they have said as
    // much, the set leads and the bottles become the reference.
    var letThemChoose = ([].concat(state.answers.format || [])[0]) === 'let';

    var setPanel = samples.length === MAX_RESULTS
      ? '<div class="quiz-panel">' +
          '<h3>' + esc(letThemChoose
            ? ui('set_let_title', 'Let them choose')
            : ui('set_own_title', 'Live with all three first')) + '</h3>' +
          '<p>' + (letThemChoose
            ? ui('set_let_body', 'Three 10&thinsp;ml bottles in one box. They find their own among them, and nobody has to guess.')
            : ui('set_own_body', 'Three 10&thinsp;ml bottles — yours, and the two either side of it. Decide once you have worn them.')) + '</p>' +
          '<button type="button" class="btn btn--primary" data-quiz-set>' +
          esc(ui('set_all_three', 'All three')) + ' &middot; ' + esc(money(sampleTotal)) + '</button>' +
        '</div>'
      : '';

    var boxPanel = box
      ? '<div class="quiz-panel">' +
          '<h3>' + esc(box.title) + '</h3>' +
          '<p>' + esc(fill('box_line', 'The discovery box built around __X__.',
            arch.name.replace(/^The /, 'the '))) + '</p>' +
          '<a class="btn btn--secondary" href="' + esc(box.url) + '" data-quiz-box="' + esc(box.handle) + '">' +
          esc(ui('box_cta', 'See the box')) + '</a>' +
        '</div>'
      : '';

    var emailPanel = root.querySelector('[data-quiz-email-template]')
      ? '<div class="quiz-panel" data-quiz-email-slot></div>' : '';

    app.innerHTML = '<section class="quiz-screen quiz-results">' +
      plate() +
      '<div class="quiz-reveal">' +
        '<p class="quiz-eyebrow">' + esc(ui('results_eyebrow', 'Your structure')) + '</p>' +
        '<h2 class="quiz-arch" tabindex="-1">' + esc(arch.name) + '</h2>' +
        '<p class="quiz-lede">' + esc(arch.line) + '</p>' +
      '</div>' +
      (letThemChoose ? setPanel + boxPanel + '<p class="quiz-note quiz-note--center">' +
        esc(ui('set_three_note', 'The three we would put in it')) + '</p>' : '') +
      '<div class="quiz-cards">' + out.results.map(cardHtml).join('') + '</div>' +
      (samples.length ? '<p class="quiz-fine">' + str('db_hint', '') + '</p>' : '') +
      (letThemChoose ? '' : setPanel + boxPanel) +
      emailPanel +
      '<div class="quiz-share">' +
        '<button type="button" class="quiz-ghost" data-share="whatsapp">' +
          esc(ui('share_whatsapp', 'Share on WhatsApp')) + '</button>' +
        '<button type="button" class="quiz-ghost" data-share="copy">' +
          esc(ui('share_copy', 'Copy link')) + '</button>' +
        '<button type="button" class="quiz-ghost" data-quiz-restart>' + esc(str('start_over', 'Start over')) + '</button>' +
      '</div></section>';

    mountEmailForm(arch);
    writeShareUrl();
    // Replacing the DOM drops focus to <body>; move it to the new heading so
    // a keyboard or screen-reader user lands on the answer.
    var heading = app.querySelector('.quiz-arch');
    if (heading) heading.focus();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function renderEmpty() {
    stage('results');
    track('quiz_no_results', { entry_path: state.path });
    app.innerHTML = '<section class="quiz-screen quiz-results">' +
      '<div class="quiz-reveal">' +
        '<p class="quiz-eyebrow">' + esc(ui('empty_eyebrow', 'A mixed signal')) + '</p>' +
        '<h2 class="quiz-arch" tabindex="-1">' +
          esc(ui('empty_title', 'Your answers pull in several directions')) + '</h2>' +
        '<p class="quiz-lede">' + esc(ui('empty_body', 'Rather than suggest a composition that does not fit, ' +
          'spend some time with the collection — and come back to the quiz when you are ready.')) + '</p>' +
      '</div>' +
      '<div class="quiz-share">' +
        '<a class="btn btn--primary" href="' + esc(str('collections_url', '/collections')) + '">' +
          esc(ui('empty_cta', 'Browse the collection')) + '</a>' +
        '<button type="button" class="quiz-ghost" data-quiz-restart>' + esc(str('start_over', 'Start over')) + '</button>' +
      '</div></section>';
  }

  // The email panel is real Shopify markup rendered by the section, cloned in
  // here rather than rebuilt, so the form keeps its authenticity token and
  // its action URL.
  function mountEmailForm(arch) {
    var tpl = root.querySelector('[data-quiz-email-template]');
    var slot = app.querySelector('[data-quiz-email-slot]');
    if (!tpl || !slot) return;
    slot.appendChild(tpl.content.cloneNode(true));
    var tags = slot.querySelector('[name="contact[tags]"]');
    if (tags && arch) tags.value = tags.value + ',' + arch.id;
  }

  /* ------------------------------------------- shareable result URL */
  // Encode the ANSWERS, not the results. A link shared today therefore re-runs
  // against current inventory instead of freezing a recommendation that may be
  // sold out by the time someone opens it.
  function writeShareUrl() {
    try {
      var packed = btoa(unescape(encodeURIComponent(JSON.stringify({ p: state.path, a: state.answers }))))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      history.replaceState(null, '', location.pathname + '?r=' + packed);
    } catch (e) { /* sharing is a bonus, never a blocker */ }
  }

  function readShareUrl() {
    var raw = new URLSearchParams(location.search).get('r');
    if (!raw) return false;
    try {
      var json = decodeURIComponent(escape(atob(raw.replace(/-/g, '+').replace(/_/g, '/'))));
      var j = JSON.parse(json);
      if (!j.p || !PATHS[j.p]) return false;
      state = { path: j.p, step: 99, answers: j.a || {}, order: pathQuestionIds(j.p) };
      renderResults();
      return true;
    } catch (e) { return false; }
  }

  function reset() {
    history.replaceState(null, '', location.pathname);
    state = { path: null, step: 0, answers: {}, order: [] };
    renderIntro();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* -------------------------------------------------------- interaction */
  root.addEventListener('click', function (e) {
    var el;

    if ((el = e.target.closest('[data-path]'))) { start(el.getAttribute('data-path')); return; }
    if (e.target.closest('[data-quiz-restart]')) { track('quiz_restart', {}); reset(); return; }

    if (e.target.closest('[data-quiz-back]')) {
      if (state.step > 0) { state.step--; track('quiz_back', { to_index: state.step + 1 }); renderQuestion(); }
      return;
    }

    var q = state.order.length ? QUESTIONS[state.order[state.step]] : null;

    if (e.target.closest('[data-quiz-skip]') && q) {
      delete state.answers[q.id];
      advance(q, null, true);
      return;
    }
    if (e.target.closest('[data-quiz-next]') && q) {
      advance(q, (state.answers[q.id] || []).join(','), false);
      return;
    }

    if ((el = e.target.closest('[data-opt]')) && q) {
      var id = el.getAttribute('data-opt');
      var cap = q.multi || 0;
      if (cap) {
        var sel = [].concat(state.answers[q.id] || []);
        sel = sel.indexOf(id) !== -1
          ? sel.filter(function (x) { return x !== id; })
          : (sel.length >= cap ? sel.slice(1).concat(id) : sel.concat(id));
        state.answers[q.id] = sel;
        repaintSelection(sel);
      } else {
        state.answers[q.id] = [id];
        advance(q, id, false);
      }
      return;
    }

    if ((el = e.target.closest('[data-add]'))) {
      addToCart([{ id: el.getAttribute('data-add'), quantity: 1 }], el, {
        event: 'quiz_add_to_cart',
        size: el.getAttribute('data-size'),
        handle: el.getAttribute('data-handle')
      });
      return;
    }

    if (e.target.closest('[data-quiz-set]')) {
      var out = run(state.answers, CATALOGUE);
      var items = out.results
        .map(function (r) { return r.variants.sample; })
        .filter(Boolean)
        .map(function (v) { return { id: v.id, quantity: 1 }; });
      if (items.length) {
        addToCart(items, e.target.closest('[data-quiz-set]'), { event: 'quiz_set_cta', size: 'trio_10ml' });
      }
      return;
    }

    if ((el = e.target.closest('[data-quiz-box]'))) {
      track('quiz_box_cta', { handle: el.getAttribute('data-quiz-box') });
      return;
    }

    if ((el = e.target.closest('[data-share]'))) {
      var channel = el.getAttribute('data-share');
      var url = location.href;
      track('quiz_share', { channel: channel });
      if (channel === 'whatsapp') {
        window.open('https://wa.me/?text=' + encodeURIComponent(document.title + ' — ' + url), '_blank', 'noopener');
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(function () { el.textContent = ui('share_copied', 'Copied'); });
      }
    }
  });

  function addToCart(items, button, meta) {
    track(meta.event, assign({ variant_ids: items.map(function (i) { return i.id; }) }, meta));
    if (window.AtharCart && window.AtharCart.addItems) {
      window.AtharCart.addItems(items, button);
    } else {
      // theme.js has not booted; fall back to the non-JS cart route.
      window.location.href = '/cart/' + items.map(function (i) { return i.id + ':' + i.quantity; }).join(',');
    }
  }

  // Post the newsletter form without leaving the results, which a normal
  // Shopify form submit would discard.
  root.addEventListener('submit', function (e) {
    var form = e.target.closest('.quiz-email__form');
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;
    fetch(form.getAttribute('action'), { method: 'POST', body: new FormData(form) })
      .then(function (res) {
        if (!res.ok) throw new Error('Signup failed');
        track('quiz_email_capture', {});
        form.outerHTML = '<p class="quiz-note quiz-note--center">' +
          esc(str('email_sent', 'Sent. Check your inbox.')) + '</p>';
      })
      .catch(function () {
        if (btn) btn.disabled = false;
        var err = form.querySelector('[data-quiz-email-error]');
        if (err) err.hidden = false;
      });
  });

  // Exposed for QA: run the matcher against any answer set from the console
  // without clicking through the interface.
  window.AtharQuiz = { run: run, narrow: narrow, catalogue: CATALOGUE, questions: QUESTIONS, paths: PATHS };

  if (!readShareUrl()) renderIntro();
}());
